import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { DashboardSidebar } from '@/components/DashboardSidebar';
import { toast } from 'sonner';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Lazy load components to avoid circular dependencies
const Overview = lazy(() => import('./dashboard/Overview'));
const Messages = lazy(() => import('./dashboard/Messages'));
const Contacts = lazy(() => import('./dashboard/Contacts'));
const Analytics = lazy(() => import('./dashboard/Analytics'));
const CateringRequests = lazy(() => import('./dashboard/CateringRequests'));
// const ViewCateringQuote = lazy(() => import('./dashboard/ViewCateringQuote'));
const EditCateringQuote = lazy(() => import('./dashboard/EditCateringQuote'));
const MenuManagement = lazy(() => import('./dashboard/MenuManagement'));
const LoadMenuData = lazy(() => import('./dashboard/LoadMenuData'));
const BlogManagement = lazy(() => import('./dashboard/BlogManagement'));
const ReservationManagement = lazy(() => import('./dashboard/ReservationManagement'));
const ReservationSettings = lazy(() => import('./dashboard/ReservationSettings'));
const Settings = lazy(() => import('./dashboard/Settings'));
const HomeSettings = lazy(() => import('./dashboard/HomeSettings'));
const SubscriptionSettings = lazy(() => import('./dashboard/SubscriptionSettings'));
const SubscriptionManagement = lazy(() => import('./dashboard/SubscriptionManagement'));
const SEOSettings = lazy(() => import('./dashboard/SEOSettings'));

const DashboardContent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { setOpenMobile, isMobile } = useSidebar();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate('/management');
      return;
    }

    // Check if user has admin or staff role
    const { data: userRoles, error: rolesError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id);

    if (rolesError) {
      console.error('Error checking user roles:', rolesError);
      toast.error("Failed to verify permissions");
      await supabase.auth.signOut();
      navigate('/management');
      return;
    }

    const hasAdminOrStaff = userRoles?.some(
      r => r.role === 'admin' || r.role === 'staff'
    );

    if (!hasAdminOrStaff) {
      toast.error("Access denied. You are not authorized to view this dashboard.");
      await supabase.auth.signOut();
      navigate('/management');
      return;
    }

    setLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex w-full">
      <DashboardSidebar onLogout={handleLogout} />
        
        <div className="flex-1 flex flex-col w-full min-w-0">
          {/* Mobile header with menu trigger */}
          {isMobile && (
            <header className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
              <div className="h-12 flex items-center px-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpenMobile(true)}
                  className="gap-2"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-5 w-5" />
                  <span className="text-sm">Menu</span>
                </Button>
              </div>
            </header>
          )}
          {/* Main Content */}
          <main className="flex-1 w-full px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6 overflow-x-auto">
            <div className="w-full">
              <Suspense fallback={<div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
                <Routes>
                  <Route path="/" element={<Overview />} />
                  <Route path="/home" element={<HomeSettings />} />
                  <Route path="/messages" element={<Messages />} />
                  <Route path="/contacts" element={<Contacts />} />
                  <Route path="/catering" element={<CateringRequests />} />
                  <Route path="/catering/edit/:id" element={<EditCateringQuote />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/menu" element={<MenuManagement />} />
                  <Route path="/menu/load-data" element={<LoadMenuData />} />
                  <Route path="/blog" element={<BlogManagement />} />
                  <Route path="/reservations" element={<ReservationManagement />} />
                  <Route path="/reservations/settings" element={<ReservationSettings />} />
                  <Route path="/subscription" element={<SubscriptionManagement />} />
                  <Route path="/subscription/stripe" element={<SubscriptionSettings />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/seo" element={<SEOSettings />} />
                </Routes>
              </Suspense>
            </div>
          </main>
        </div>
    </div>
  );
};

const Dashboard = () => {
  return (
    <SidebarProvider defaultOpen={true}>
      <DashboardContent />
    </SidebarProvider>
  );
};

export default Dashboard;
